import { api } from "@/api";
import { AxiosInstance, AxiosStatic } from "axios";
import { useNotificationsStore } from "@/stores/notifications";
import { useMjmlStore } from "@/stores/mjmlStore";
import { useUserStore } from "@/stores/userStore";
import { useCommentsStore } from "@/stores/commentsStore";
import {
  IConversation,
  ConversationsSchema,
  ConversationSchema,
} from "@/schemas/comments/commentsSchema";
import { getCurrentInstance, ref } from "vue";
import { getEmbedderHostname } from "@topol/utils";

export default () => {
  const app = getCurrentInstance();

  if (app === null) {
    throw new Error("App is not defined");
  }

  const $http: AxiosStatic | AxiosInstance =
    app.appContext.config.globalProperties.$http;

  const notificationsStore = useNotificationsStore();
  const mjmlStore = useMjmlStore();
  const userStore = useUserStore();
  const commentsStore = useCommentsStore();
  const loadingConversation = ref(false);
  const addCommentLoading = ref(false);

  const loadAllComments = () => {
    const params: Record<string, any> = getParams();
    params.sort_by = commentsStore.sortBy;
    params.show_resolved = commentsStore.showResolved;

    if (api.CONVERSATIONS) {
      $http
        .get(api.CONVERSATIONS, { params: params })
        .then((res) => {
          if (res.data.success === true) {
            const { error, data, success } = ConversationsSchema.safeParse(
              res.data.data
            );
            if (!success) {
              console.error("Loading comments failed", error.issues);
              notificationsStore.createNotification({
                title: "Loading comments failed",
                type: "error",
                text: error.message,
              });
              commentsStore.allComments = [];
            } else {
              commentsStore.allComments = data;
            }
          } else {
            notificationsStore.createNotification({
              title: "Loading comments failed",
              type: "error",
              text: "",
            });
            commentsStore.allComments = [];
          }
          setUnread();
        })
        .catch(() => {
          notificationsStore.createNotification({
            title: "Loading comments failed",
            type: "error",
            text: "",
          });
          commentsStore.allComments = [];
          setUnread();
        });
    }
  };

  const loadConversation = async (key: string, withLoadingState: boolean) => {
    loadingConversation.value = withLoadingState;
    const params = getParams();
    let data: IConversation | null | undefined = null;

    if (api.CONVERSATIONS) {
      try {
        const resp = await $http.get(api.CONVERSATIONS + "/" + key, {
          params: params,
        });
        if (resp.data.success === true) {
          const {
            error,
            data: successData,
            success,
          } = ConversationSchema.safeParse(resp.data.data);
          if (!success && resp.data.data) {
            console.error("Loading conversation failed", error.issues);
            notificationsStore.createNotification({
              title: "Loading conversation failed",
              type: "error",
              text: error.message,
            });
            data = null;
          } else {
            data = successData;
          }
        } else {
          data = null;
        }
      } catch (error: any) {
        data = null;
      }
    }
    loadingConversation.value = false;
    return data;
  };

  const addComment = async (
    conversationKey: string,
    type: string,
    content: string,
    mentions: Array<string>
  ) => {
    addCommentLoading.value = true;
    if (api.CONVERSATIONS) {
      try {
        await $http.post(api.CONVERSATIONS + "/" + conversationKey, {
          template_id: mjmlStore.templateId,
          entity_id: mjmlStore.authorize.userId,
          current_user_id: userStore.currentUser.userId,
          hostname: getEmbedderHostname(),
          key: mjmlStore.authorize.apiKey,
          type: type,
          content: content,
          mentions: mentions,
        });
        addCommentLoading.value = false;
        return true;
      } catch (error: any) {
        notificationsStore.createNotification({
          title: "Sending comments failed",
          type: "error",
          text: "",
        });
        addCommentLoading.value = false;
        return false;
      }
    }
    addCommentLoading.value = false;
  };

  const editComment = (
    commentId: string | number,
    content: string,
    conversationKey: string,
    mentions: Array<string>
  ) => {
    if (api.COMMENTS) {
      $http
        .post(api.COMMENTS + "/" + commentId, {
          template_id: mjmlStore.templateId,
          entity_id: mjmlStore.authorize.userId,
          current_user_id: userStore.currentUser.userId,
          hostname: getEmbedderHostname(),
          key: mjmlStore.authorize.apiKey,
          content: content,
          mentions: mentions,
        })
        .then(() => {
          refreshComments(conversationKey);
          return true;
        })
        .catch(() => {
          notificationsStore.createNotification({
            title: "Sending comments failed",
            type: "error",
            text: "",
          });
          return false;
        });
    }
  };

  const reactToComments = (
    commentId: string | number,
    type: string,
    conversationKey: string
  ) => {
    if (api.COMMENTS) {
      $http
        .post(api.COMMENTS + "/" + commentId, {
          template_id: mjmlStore.templateId,
          entity_id: mjmlStore.authorize.userId,
          current_user_id: userStore.currentUser.userId,
          hostname: getEmbedderHostname(),
          key: mjmlStore.authorize.apiKey,
          react: type,
        })
        .then(() => {
          refreshComments(conversationKey);
          return true;
        })
        .catch(() => {
          notificationsStore.createNotification({
            title: "Sending comments failed",
            type: "error",
            text: "",
          });
          return false;
        });
    }
  };

  const deleteComment = async (
    commentId: string | number,
    conversationKey: string
  ) => {
    commentsStore.loading = true;
    const params = getParams();
    if (api.COMMENTS) {
      try {
        await $http.delete(api.COMMENTS + "/" + commentId, {
          params: params,
        });
        refreshComments(conversationKey);
        commentsStore.loading = false;
        return true;
      } catch (error: any) {
        notificationsStore.createNotification({
          title: "Deleting comment failed",
          type: "error",
          text: "",
        });
        commentsStore.loading = false;
        return false;
      }
    }
    commentsStore.loading = false;
  };

  const deleteConversation = async (conversationKey: string) => {
    const params = getParams();
    if (api.CONVERSATIONS) {
      try {
        await $http.delete(api.CONVERSATIONS + "/" + conversationKey, {
          params: params,
        });
        refreshComments(conversationKey);
        commentsStore.openDetail = false;
        return true;
      } catch (error: any) {
        notificationsStore.createNotification({
          title: "Deleting conversation failed",
          type: "error",
          text: "",
        });
        return false;
      }
    }
  };

  const refreshComments = (key: string) => {
    loadAllComments();
    window.$event.emit("refreshConversation", key);
  };

  const markCommentAsUnread = async (commentId: string | number) => {
    if (api.COMMENTS) {
      try {
        await $http.post(api.COMMENTS + "/" + commentId, {
          template_id: mjmlStore.templateId,
          entity_id: mjmlStore.authorize.userId,
          current_user_id: userStore.currentUser.userId,
          hostname: getEmbedderHostname(),
          key: mjmlStore.authorize.apiKey,
          action: "mark_as_unread",
        });
        loadAllComments();
        return true;
      } catch (error: any) {
        notificationsStore.createNotification({
          title: "Unreading conversation failed",
          type: "error",
          text: "",
        });
        return false;
      }
    }
  };

  const markCommentAsRead = async (commentId: string | number) => {
    if (api.COMMENTS) {
      try {
        await $http.post(api.COMMENTS + "/" + commentId, {
          template_id: mjmlStore.templateId,
          entity_id: mjmlStore.authorize.userId,
          current_user_id: userStore.currentUser.userId,
          hostname: getEmbedderHostname(),
          key: mjmlStore.authorize.apiKey,
          action: "mark_as_read",
        });
        loadAllComments();
        return true;
      } catch (error: any) {
        notificationsStore.createNotification({
          title: "Reading conversation failed",
          type: "error",
          text: "",
        });
        return false;
      }
    }
  };

  const resolveConversation = async (
    conversationKey: string,
    resolved: boolean
  ) => {
    if (api.CONVERSATIONS) {
      try {
        await $http.patch(api.CONVERSATIONS + "/" + conversationKey, {
          template_id: mjmlStore.templateId,
          entity_id: mjmlStore.authorize.userId,
          current_user_id: userStore.currentUser.userId,
          hostname: getEmbedderHostname(),
          key: mjmlStore.authorize.apiKey,
          resolved: resolved,
        });
        refreshComments(conversationKey);
        return true;
      } catch (error: any) {
        notificationsStore.createNotification({
          title: "Resolving conversation failed",
          type: "error",
          text: "",
        });
        return false;
      }
    }
  };

  function getParams() {
    return {
      key: mjmlStore.authorize.apiKey,
      hostname: getEmbedderHostname(),
      template_id: mjmlStore.templateId,
      entity_id: mjmlStore.authorize.userId,
      current_user_id: userStore.currentUser.userId,
    };
  }

  function setUnread() {
    let isUnreadConv = false;
    for (const conversation of commentsStore.allComments) {
      if (
        conversation.comments.length > 0 &&
        [...conversation.comments].reverse()[0].created_by.user_id !==
          userStore.currentUser.userId &&
        [...conversation.comments]
          .reverse()[0]
          .read_by.indexOf(userStore.currentUser.userId) === -1
      ) {
        isUnreadConv = true;
      }
    }

    commentsStore.unread = isUnreadConv;
  }

  return {
    loadingConversation,
    addCommentLoading,
    loadAllComments,
    loadConversation,
    addComment,
    editComment,
    reactToComments,
    deleteComment,
    deleteConversation,
    refreshComments,
    markCommentAsUnread,
    markCommentAsRead,
    resolveConversation,
  };
};
